import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

const fmt = (v, type) => {
  if (type === "inr") {
    const abs = Math.abs(v);
    if (abs >= 1_00_00_000) return `${(v / 1_00_00_000).toFixed(2)} Cr`;
    if (abs >= 1_00_000) return `${(v / 1_00_000).toFixed(2)} L`;
    return v.toLocaleString("en-IN");
  }
  if (type === "pct") {
    if (Math.abs(v) > 999) return "N/M";
    return `${v}%`;
  }
  if (type === "ratio") {
    if (Math.abs(v) > 99) return "N/M";
    return `${v}x`;
  }
  if (type === "days") {
    if (Math.abs(v) > 999) return "N/M";
    return `${v} days`;
  }
  return v;
};

const TrendIcon = ({ value }) => {
  if (value > 0) return <TrendingUp className="w-4 h-4 text-emerald-600" />;
  if (value < 0) return <TrendingDown className="w-4 h-4 text-red-500" />;
  return <Minus className="w-4 h-4 text-muted-foreground" />;
};

const KPICard = ({ label, value, type, benchmark, icon: Icon }) => {
  const display = fmt(value, type);
  const isBad = benchmark && (
    (benchmark.direction === "higher" && value < benchmark.threshold) ||
    (benchmark.direction === "lower" && value > benchmark.threshold)
  );
  return (
    <Card className={`card-industrial ${isBad ? "border-red-300" : ""}`} data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</span>
          {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
        </div>
        <p className="text-2xl font-bold tracking-tight">{display}</p>
        {benchmark && (
          <p className={`text-xs mt-1 ${isBad ? "text-red-500" : "text-emerald-600"}`}>
            Target: {benchmark.direction === "higher" ? ">" : "<"} {fmt(benchmark.threshold, type)}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const KPIDashboard = ({ kpis }) => {
  if (!kpis) return null;

  const sections = [
    {
      title: "Profitability",
      items: [
        { label: "YTD Revenue", key: "ytd_revenue", type: "inr" },
        { label: "YTD Net Income", key: "ytd_net_income", type: "inr" },
        { label: "YTD EBITDA", key: "ytd_ebitda", type: "inr" },
        { label: "Free Cash Flow", key: "free_cash_flow", type: "inr" },
        { label: "Revenue Growth", key: "revenue_growth", type: "pct" },
        { label: "Gross Margin", key: "gross_margin", type: "pct", benchmark: { threshold: 30, direction: "higher" } },
        { label: "Operating Margin", key: "operating_margin", type: "pct", benchmark: { threshold: 10, direction: "higher" } },
        { label: "Net Margin", key: "net_profit_margin", type: "pct", benchmark: { threshold: 5, direction: "higher" } },
        { label: "EBITDA Margin", key: "ebitda_margin", type: "pct" },
      ],
    },
    {
      title: "Liquidity & Leverage",
      items: [
        { label: "Current Ratio", key: "current_ratio", type: "ratio", benchmark: { threshold: 1.5, direction: "higher" } },
        { label: "Quick Ratio", key: "quick_ratio", type: "ratio", benchmark: { threshold: 1.0, direction: "higher" } },
        { label: "Interest Coverage", key: "interest_coverage", type: "ratio", benchmark: { threshold: 3.0, direction: "higher" } },
        { label: "Debt-to-Equity", key: "debt_to_equity", type: "ratio", benchmark: { threshold: 1.5, direction: "lower" } },
        { label: "Working Capital", key: "working_capital", type: "inr" },
      ],
    },
    {
      title: "Returns & Efficiency",
      items: [
        { label: "ROIC", key: "roic", type: "pct", benchmark: { threshold: 12.0, direction: "higher" } },
        { label: "ROE", key: "roe", type: "pct" },
        { label: "ROA", key: "roa", type: "pct" },
        { label: "Fixed Asset Turnover", key: "fixed_asset_turnover", type: "ratio", benchmark: { threshold: 1.5, direction: "higher" } },
        { label: "DSO", key: "dso", type: "days" },
        { label: "DIO", key: "dio", type: "days" },
        { label: "DPO", key: "dpo", type: "days" },
        { label: "Cash Conv. Cycle", key: "cash_conversion_cycle", type: "days" },
      ],
    },
  ];

  return (
    <div className="space-y-6" data-testid="kpi-dashboard">
      {sections.map((sec) => (
        <div key={sec.title}>
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">{sec.title}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {sec.items.map((item) => (
              <KPICard
                key={item.key}
                label={item.label}
                value={kpis[item.key] ?? 0}
                type={item.type}
                benchmark={item.benchmark}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default KPIDashboard;
